import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { api, formatApiErrorDetail, formatUSD } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { DesignerLayout } from "@/components/layout/DesignerLayout";
import { toast } from "sonner";
import { User, Lock, Building, EnvelopeSimple, Coins, Receipt, NewspaperClipping } from "@phosphor-icons/react";

const ROLE_BADGE = {
  admin: { label: "Studio Admin", style: "border-white/30 text-white" },
  designer: { label: "Designer", style: "border-[#FF2D2D] text-[#FF2D2D]" },
  client: { label: "Client", style: "border-[#a3a3a3] text-[#a3a3a3]" },
};

const STATUS_TABS = [
  { id: "pending", label: "Pending" },
  { id: "paid", label: "Paid" },
  { id: "all", label: "All" },
];

function ChargesHistory() {
  const [charges, setCharges] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/charges");
        setCharges(data || []);
      } catch (err) {
        // 401/403 are handled by axios interceptor / ProtectedRoute; surface anything else.
        if (err.response?.status && err.response.status >= 500) {
          toast.error("Could not load charges history");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const list = charges.filter((c) => tab === "all" || c.status === tab);
  const totalPending = charges.filter((c) => c.status === "pending").reduce((s, c) => s + (c.amount || 0), 0);
  const totalPaid = charges.filter((c) => c.status === "paid").reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <div className="border border-[#262626] bg-[#0a0a0a] p-6 mb-8" data-testid="charges-history">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Receipt size={18} className="text-[#FF2D2D]" weight="fill" />
          <h2 className="font-display text-2xl uppercase">Charges & receipts</h2>
        </div>
        <div className="text-xs text-[#a3a3a3] flex items-center gap-3 flex-wrap">
          <span>Pending: <strong className="text-[#FF2D2D]">{formatUSD(totalPending)}</strong></span>
          <span>Paid: <strong className="text-[#22c55e]">{formatUSD(totalPaid)}</strong></span>
        </div>
      </div>

      <div className="flex gap-1 mb-4">
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`charges-tab-${t.id}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
              tab === t.id
                ? "border-[#FF2D2D] bg-[#FF2D2D] text-white"
                : "border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#a3a3a3] p-6 text-center text-sm">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-[#a3a3a3] p-6 text-center text-sm" data-testid="charges-empty">
          {tab === "pending" ? "No pending charges. You're all caught up." :
           tab === "paid" ? "No paid charges yet — your receipts will appear here." :
           "No custom charges have been issued to your account."}
        </div>
      ) : (
        <div className="divide-y divide-[#262626] -mx-2">
          {list.map((c) => (
            <div
              key={c.id}
              className="px-2 py-3 flex items-center justify-between gap-3 flex-wrap"
              data-testid={`account-charge-${c.id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{c.description}</div>
                <div className="text-xs text-[#a3a3a3] mt-0.5">
                  {c.status === "paid" && c.paid_at
                    ? <>Paid {new Date(c.paid_at).toLocaleDateString()}</>
                    : <>Issued {new Date(c.created_at).toLocaleDateString()}</>
                  }
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-display text-lg tabular-nums">{formatUSD(c.amount)}</span>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${
                  c.status === "paid" ? "border-[#22c55e]/40 text-[#22c55e]" :
                  c.status === "cancelled" ? "border-[#404040] text-[#a3a3a3]" :
                  "border-[#FF2D2D] text-[#FF2D2D]"
                }`}>{c.status}</span>
                {c.status === "pending" && (
                  <Link
                    to={`/charges/${c.id}`}
                    data-testid={`account-pay-${c.id}`}
                    className="btn-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider"
                  >
                    Pay
                  </Link>
                )}
                {c.status === "paid" && (
                  <Link
                    to={`/charges/${c.id}`}
                    data-testid={`account-receipt-${c.id}`}
                    className="text-xs text-[#a3a3a3] hover:text-[#FF2D2D] underline"
                  >
                    View
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccountInner() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [companyName, setCompanyName] = useState(user?.company_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setEmail(user.email || "");
    setCompanyName(user.company_name || "");
    setPhone(user.phone || "");
  }, [user]);

  if (!user) return null;

  const isClient = user.role === "client";
  const badge = ROLE_BADGE[user.role] || ROLE_BADGE.client;

  const saveProfile = async (e) => {
    e.preventDefault();
    const body = {};
    if (name && name !== user.name) body.name = name;
    if (email && email.toLowerCase() !== user.email) body.email = email;
    if (isClient && companyName !== (user.company_name || "")) body.company_name = companyName;
    if (isClient && phone !== (user.phone || "")) body.phone = phone;
    if (Object.keys(body).length === 0) {
      toast.message("Nothing changed");
      return;
    }
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", body);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (newPw !== confirmPw) { toast.error("Passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Change failed");
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Account</div>
        <h1 className="font-display text-5xl mt-2">Your settings</h1>
        <div className="flex flex-wrap items-center gap-3 mt-4 text-xs">
          <span className={`uppercase tracking-wider px-2 py-1 border ${badge.style}`}>{badge.label}</span>
          {isClient && (
            <span className="flex items-center gap-1 text-[#a3a3a3]">
              <Coins size={12} weight="fill" className="text-[#FF2D2D]" />
              {(user.points_balance ?? 0).toFixed(2)} pts
            </span>
          )}
        </div>
      </div>

      {/* Profile */}
      <form onSubmit={saveProfile} className="border border-[#262626] bg-[#0a0a0a] p-6 mb-8" data-testid="profile-form">
        <div className="flex items-center gap-2 mb-4">
          <User size={18} className="text-[#FF2D2D]" />
          <h2 className="font-display text-2xl uppercase">Profile</h2>
        </div>

        <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Full name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          minLength={1}
          data-testid="profile-name"
          className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
        />

        <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2 flex items-center gap-2">
          <EnvelopeSimple size={12} /> Email
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="profile-email"
          className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
        />
        <div className="text-xs text-[#666] mt-1">Email change takes effect immediately.</div>

        {isClient && (
          <>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2 flex items-center gap-2">
              <Building size={12} /> Company / organization <span className="text-[#666] normal-case tracking-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              data-testid="profile-company"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />
            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">Phone <span className="text-[#666] normal-case tracking-normal">(optional)</span></label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="profile-phone"
              placeholder="+1 555 123 4567"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />
          </>
        )}

        <button
          type="submit"
          disabled={savingProfile}
          data-testid="profile-save"
          className="btn-primary mt-6 px-5 py-3 text-sm font-bold uppercase tracking-wider"
        >
          {savingProfile ? "Saving…" : "Save changes"}
        </button>
      </form>

      {/* Client charges history */}
      {isClient && <ChargesHistory />}

      {/* Monthly digest opt-in (clients only) */}
      {isClient && <DigestPreference />}

      {/* Password */}
      <form onSubmit={changePassword} className="border border-[#262626] bg-[#0a0a0a] p-6" data-testid="password-form">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={18} className="text-[#FF2D2D]" />
          <h2 className="font-display text-2xl uppercase">Change password</h2>
        </div>

        <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Current password</label>
        <input
          type="password"
          value={currentPw}
          onChange={(e) => setCurrentPw(e.target.value)}
          required
          autoComplete="current-password"
          data-testid="password-current"
          className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
        />

        <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">New password</label>
        <input
          type="password"
          value={newPw}
          onChange={(e) => setNewPw(e.target.value)}
          required
          minLength={6}
          autoComplete="new-password"
          data-testid="password-new"
          className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
        />
        <div className="text-xs text-[#666] mt-1">Min 6 characters.</div>

        <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">Confirm new password</label>
        <input
          type="password"
          value={confirmPw}
          onChange={(e) => setConfirmPw(e.target.value)}
          required
          minLength={6}
          autoComplete="new-password"
          data-testid="password-confirm"
          className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
        />

        <button
          type="submit"
          disabled={savingPw}
          data-testid="password-save"
          className="btn-primary mt-6 px-5 py-3 text-sm font-bold uppercase tracking-wider"
        >
          {savingPw ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}

function DigestPreference() {
  const { user, refresh } = useAuth();
  const [optOut, setOptOut] = useState(Boolean(user?.monthly_digest_opt_out));
  const [saving, setSaving] = useState(false);

  const toggle = async (next) => {
    setSaving(true);
    try {
      await api.patch("/digest/opt-out", { opt_out: next });
      setOptOut(next);
      toast.success(next ? "Monthly digest disabled" : "Monthly digest enabled");
      refresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#262626] bg-[#0a0a0a] p-6 mb-8" data-testid="digest-preference">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <NewspaperClipping size={20} className="text-[#FF2D2D] shrink-0 mt-0.5" weight="fill" />
          <div className="min-w-0">
            <h2 className="font-display text-xl uppercase">Monthly digest</h2>
            <p className="text-sm text-[#a3a3a3] mt-1 max-w-lg">
              A short recap on the 1st of every month — projects delivered, points earned,
              referrals, and reviews. Sent only if there's activity worth sharing.
            </p>
          </div>
        </div>
        <label className="inline-flex items-center gap-3 cursor-pointer self-center">
          <span className="text-xs text-[#a3a3a3] uppercase tracking-wider">
            {optOut ? "Off" : "On"}
          </span>
          <button
            type="button"
            onClick={() => toggle(!optOut)}
            disabled={saving}
            data-testid="digest-toggle"
            aria-pressed={!optOut}
            className={`relative w-11 h-6 transition-colors ${optOut ? "bg-[#262626]" : "bg-[#FF2D2D]"} ${saving ? "opacity-50" : ""}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${optOut ? "translate-x-0" : "translate-x-5"}`}
            />
          </button>
        </label>
      </div>
    </div>
  );
}

export default function Account() {
  const { user } = useAuth();
  if (!user) return null;

  const Layout = user.role === "admin" ? AdminLayout : user.role === "designer" ? DesignerLayout : ClientLayout;
  return (
    <Layout>
      <AccountInner />
    </Layout>
  );
}
